import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SvgIcon,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import TooltipMUI6 from 'src/components/TooltipMUI6';

import { ReactComponent as CopyIcon } from 'src/assets/images/copy-icon.svg';
import copyTextStyle from 'src/components/CopyText/copyText.style';

/**
 * CopyText with more than one thing to copy.
 *
 * Same look and clipboard mechanics as CopyText, but the click opens a menu of
 * labelled options instead of copying a single value. Built as a sibling
 * rather than an option on CopyText because that component has 17 import
 * sites and none of them want a menu.
 *
 * options: [{ label, value, icon }] — icon optional.
 */
function CopyTextMenu({
  classes,
  text,
  options,
  tooltipCopyText,
  tooltipCopiedText,
}) {
  const anchorRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTextCopied, setIsTextCopied] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);

  const copyableOptions = (options || []).filter((option) => option && option.value);

  const handleOpenMenu = (e) => {
    setOpenTooltip(false);
    setIsMenuOpen(true);
    e.stopPropagation();
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const handleCopy = (value) => (e) => {
    navigator.clipboard.writeText(value);
    setIsMenuOpen(false);
    setIsTextCopied(true);
    setOpenTooltip(true);
    setTimeout(() => {
      setOpenTooltip(false);
      setTimeout(() => {
        setIsTextCopied(false);
      }, 200);
    }, 2500);
    e.stopPropagation();
  };

  const handleMouseLeave = () => {
    if (!isTextCopied) {
      setOpenTooltip(false);
    }
  };

  const handleMouseEnter = () => {
    if (!isMenuOpen) {
      setOpenTooltip(true);
    }
  };

  // No clipboard (older or non-secure contexts) or nothing to copy — fall back
  // to plain text, exactly as CopyText does.
  if (!text || !navigator.clipboard || copyableOptions.length === 0) {
    return text || null;
  }

  return (
    <>
      <TooltipMUI6
        title={!isTextCopied ? tooltipCopyText : tooltipCopiedText}
        open={openTooltip}
      >
        <Button
          className={classes.root}
          onClick={handleOpenMenu}
          onMouseLeave={handleMouseLeave}
          onMouseEnter={handleMouseEnter}
          ref={anchorRef}
        >
          {text}
          <SvgIcon
            className="svgIcon"
            component={CopyIcon}
            viewBox="0 0 448 512"
          />
        </Button>
      </TooltipMUI6>
      <Menu
        anchorEl={anchorRef.current}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {copyableOptions.map((option) => (
          <MenuItem
            key={option.label}
            onClick={handleCopy(option.value)}
          >
            {option.icon && (
              <ListItemIcon>
                {option.icon}
              </ListItemIcon>
            )}
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

CopyTextMenu.propTypes = {
  classes: PropTypes.object.isRequired,
  text: PropTypes.node,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    icon: PropTypes.node,
  })),
  tooltipCopyText: PropTypes.string,
  tooltipCopiedText: PropTypes.string,
};

CopyTextMenu.defaultProps = {
  text: null,
  options: [],
  tooltipCopyText: 'Click to copy',
  tooltipCopiedText: 'Copied',
};

export default withStyles(CopyTextMenu, copyTextStyle);
